import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import UnifiedFiles from 'features/bpDetails/Components/UnifiedFiles'
import { IDENTITY_FOLDER_ID } from 'features/bpDetails/Components/files/IdentityDocumentsFolder'
import { listPortfolioEstates } from 'services/estateService'
import {
  loadPortfolioFilesByFolderRoutine,
  loadPortfolioFilesByCategoryRoutine,
  resetPortfolioDocumentsRoutine
} from 'ducks/portfolioDocuments/actions'
import {
  selectLoadedPortfolioFolders,
  selectLoadingPortfolioFolders,
  selectPortfolioLoadingProgress,
  selectPortfolioCursor,
  selectPortfolioHasMore
} from 'ducks/portfolioDocuments/selectors'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import { getPortfolioFileCounts } from 'services/portfolioDocumentsService'
import { getFileThumbnails } from 'services/fileService'
import { updateFileThumbnailsRoutine } from 'ducks/files/actions'
import { getCurrentUser } from 'features/auth/ducks/selectors'

const EMPTY_QUERY = {}

const normalizeThumbnailKey = str => str.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()

const EMPTY_FILES = []

const PortfolioDocuments = ({ portfolioId, buildingPassports = [] }) => {
  const dispatch = useDispatch()
  const fileCategories = useSelector(selectFileCategories)
  const loadedFolders = useSelector(selectLoadedPortfolioFolders)
  const loadingFolders = useSelector(selectLoadingPortfolioFolders)
  const loadingProgress = useSelector(selectPortfolioLoadingProgress)
  const currentUser = useSelector(getCurrentUser)
  const store = useStore()
  const requestedThumbnailIdsRef = useRef(new Set())
  const hideDuplicates = currentUser?.hideDuplicateFiles ?? true
  const mutationVersion = useSelector(state => state.files.mutationVersion || 0)
  const prevMutationRef = useRef(mutationVersion)
  const prevHideDupRef = useRef(hideDuplicates)

  const [selectedFolderId, setSelectedFolderId] = useState(null)
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)
  const currentSelectionKey = selectedCategoryId || selectedFolderId
  const filesSource = useSelector(state =>
    currentSelectionKey
      ? state.portfolioDocuments.portfolioFilesByFolder[currentSelectionKey] || EMPTY_FILES
      : EMPTY_FILES
  )
  const cursor = useSelector(selectPortfolioCursor(currentSelectionKey))
  const hasMore = useSelector(selectPortfolioHasMore(currentSelectionKey))
  const isLoadingCurrentSelection = loadingFolders.has(currentSelectionKey)

  const [counts, setCounts] = useState(null)
  const [estates, setEstates] = useState([])

  const countsSeqRef = useRef(0)
  const refetchCounts = useCallback(() => {
    if (!portfolioId) return
    const seq = ++countsSeqRef.current
    getPortfolioFileCounts({ portfolioId, query: hideDuplicates ? { 'filter[hide_duplicates]': 1 } : {} })
      .then(res => { if (seq === countsSeqRef.current) setCounts(res?.data?.data || null) })
      .catch(() => {})
  }, [portfolioId, hideDuplicates])

  useEffect(() => { refetchCounts() }, [refetchCounts])

  useEffect(() => {
    if (!portfolioId) return
    let active = true
    listPortfolioEstates(portfolioId)
      .then(res => { if (active) setEstates(res?.data?.data || []) })
      .catch(() => {})
    return () => { active = false }
  }, [portfolioId])

  const customBuildings = useMemo(() => {
    const byId = new Map()
    buildingPassports.forEach(bp => {
      byId.set(bp.id, {
        id: bp.id,
        name: bp.name,
        address: [bp.street, bp.city].filter(Boolean).join(', ') || undefined,
        estateName: undefined
      })
    })
    estates.forEach(estate => {
      (estate.buildingPassports || []).forEach(member => {
        const existing = byId.get(member.id) || {
          id: member.id,
          name: member.name,
          address: [member.street, member.city].filter(Boolean).join(', ') || undefined
        }
        byId.set(member.id, { ...existing, estateName: estate.name })
      })
    })
    return Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }, [buildingPassports, estates])

  useEffect(() => {
    if (!filesSource || filesSource.length === 0) return

    const currentThumbnails = store.getState().files.thumbnailsById || {}
    const requested = requestedThumbnailIdsRef.current
    const filesNeedingThumbnails = filesSource.filter(
      file =>
        file.buildingPassport?.id &&
        currentThumbnails[file.id] === undefined &&
        !requested.has(file.id)
    )
    if (filesNeedingThumbnails.length === 0) return

    const fileIdsByBp = {}
    filesNeedingThumbnails.forEach(file => {
      requested.add(file.id)
      const bpId = file.buildingPassport.id
      fileIdsByBp[bpId] = fileIdsByBp[bpId] || []
      fileIdsByBp[bpId].push(file.id)
    })

    let cancelled = false

    const loadThumbnailsForBp = async (bpId, fileIds) => {
      for (let i = 0; i < fileIds.length; i += 50) {
        const batch = fileIds.slice(i, i + 50)
        try {
          const response = await getFileThumbnails(bpId, batch)
          if (cancelled) return
          const thumbnails = response?.data?.thumbnails || {}
          const batchUpdates = batch.map(fileId => {
            const normalizedFileId = normalizeThumbnailKey(fileId)
            const matchingKey = Object.keys(thumbnails).find(
              key => normalizeThumbnailKey(key) === normalizedFileId
            )
            return { id: fileId, thumbnailUrl: (matchingKey && thumbnails[matchingKey]) || null }
          })
          dispatch(updateFileThumbnailsRoutine.success(batchUpdates))
        } catch (e) {
          batch.forEach(fileId => requested.delete(fileId))
        }
      }
    }

    Object.entries(fileIdsByBp).forEach(([bpId, fileIds]) => {
      loadThumbnailsForBp(bpId, fileIds)
    })

    return () => {
      cancelled = true
    }
  }, [filesSource, dispatch, store])

  const onLoadFolder = useCallback(folderId => {
    dispatch(loadPortfolioFilesByFolderRoutine({ portfolioId, folderId, fileCategories }))
  }, [dispatch, portfolioId, fileCategories])

  const onLoadCategory = useCallback(categoryId => {
    dispatch(loadPortfolioFilesByCategoryRoutine({ portfolioId, categoryId }))
  }, [dispatch, portfolioId])

  const lastAggregateQuery = useSelector(state => state.portfolioDocuments.lastAggregateQuery || EMPTY_QUERY)
  const onLoadAll = useCallback(() => {
    if (selectedCategoryId) {
      dispatch(loadPortfolioFilesByCategoryRoutine({ portfolioId, categoryId: selectedCategoryId, ...lastAggregateQuery }))
    } else if (selectedFolderId === 'uncategorised' || selectedFolderId === 'all_files') {
      dispatch(loadPortfolioFilesByCategoryRoutine({ portfolioId, categoryId: selectedFolderId, ...lastAggregateQuery }))
    } else if (selectedFolderId && selectedFolderId !== IDENTITY_FOLDER_ID) {
      dispatch(loadPortfolioFilesByFolderRoutine({ portfolioId, folderId: selectedFolderId, fileCategories, ...lastAggregateQuery }))
    }
  }, [dispatch, portfolioId, selectedCategoryId, selectedFolderId, fileCategories, lastAggregateQuery])

  const onLoadMore = useCallback(() => {
    if (!currentSelectionKey || !hasMore || isLoadingCurrentSelection) return
    if (selectedCategoryId) {
      dispatch(loadPortfolioFilesByCategoryRoutine({ portfolioId, categoryId: selectedCategoryId, cursor }))
    } else if (selectedFolderId === 'uncategorised' || selectedFolderId === 'all_files') {
      dispatch(loadPortfolioFilesByCategoryRoutine({ portfolioId, categoryId: selectedFolderId, cursor }))
    } else if (selectedFolderId && selectedFolderId !== IDENTITY_FOLDER_ID) {
      dispatch(loadPortfolioFilesByFolderRoutine({ portfolioId, folderId: selectedFolderId, fileCategories, cursor }))
    }
  }, [dispatch, portfolioId, selectedCategoryId, selectedFolderId, fileCategories, cursor, hasMore, isLoadingCurrentSelection, currentSelectionKey])

  useEffect(() => {
    if (selectedFolderId !== 'all_files') return
    if (loadedFolders.has('all_files') || loadingFolders.has('all_files')) return
    dispatch(loadPortfolioFilesByCategoryRoutine({ portfolioId, categoryId: 'all_files' }))
  }, [selectedFolderId, loadedFolders, loadingFolders, portfolioId, dispatch])

  useEffect(() => {
    if (prevMutationRef.current === mutationVersion) return
    prevMutationRef.current = mutationVersion
    onLoadAll()
    refetchCounts()
  }, [mutationVersion, onLoadAll, refetchCounts])

  useEffect(() => {
    if (prevHideDupRef.current === hideDuplicates) return
    prevHideDupRef.current = hideDuplicates
    onLoadAll()
  }, [hideDuplicates, onLoadAll])

  useEffect(() => {
    return () => { dispatch(resetPortfolioDocumentsRoutine.trigger()) }
  }, [portfolioId, dispatch])

  const onSelectFolder = useCallback(folderId => {
    setSelectedFolderId(folderId)
  }, [])

  const onSelectCategory = useCallback(categoryId => {
    setSelectedCategoryId(categoryId)
  }, [])

  if (!portfolioId) return null

  return (
    <UnifiedFiles
      key={`portfolio:${portfolioId}`}
      context='portfolio-aggregate'
      stateKeyId={`portfolio:${portfolioId}`}
      canEdit={false}
      filesSource={filesSource}
      countsSource={counts}
      loadedFolders={loadedFolders}
      loadingFolders={loadingFolders}
      allFilesLoaded={false}
      isLoadingFiles={false}
      loadingProgress={loadingProgress}
      onLoadFolder={onLoadFolder}
      onLoadCategory={onLoadCategory}
      onLoadAll={onLoadAll}
      onSelectFolder={onSelectFolder}
      onSelectCategory={onSelectCategory}
      showCustomTab={true}
      customBuildings={customBuildings}
      hasMore={hasMore}
      loadingMore={isLoadingCurrentSelection}
      onLoadMore={onLoadMore}
    />
  )
}

export default PortfolioDocuments
